import {useEffect, useState} from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {rowIdAccessor} from 'data-frames/util/createAccessors';
import {DataFrameSortInputVariable} from 'data-frames/util/toDataFrameSortInput';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {pushToast} from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import sortedBy from 'util/sortedBy';
import {Override} from 'util/Types';

import {
  GetPersistedDataFrameRowsById,
  GetPersistedDataFrameRowsById_dataFrame_DataFrameNode as RawGetPersistedDataFrameRowsByIdDataFrameNode,
  GetPersistedDataFrameRowsById_dataFrame_InaccessibleInfoNode as GetPersistedDataFrameRowsByIdInaccessibleInfoNode,
  GetPersistedDataFrameRowsByIdVariables,
} from './__generated/GetPersistedDataFrameRowsById';
import {GET_PERSISTED_DATA_FRAME_ROWS_BY_ID_QUERY} from './GetPersistedDataFrameRowsByIdQuery';

type GetPersistedDataFrameRowsByIdDataFrameNode = Override<
  RawGetPersistedDataFrameRowsByIdDataFrameNode,
  {dataFrameJson: DataFrame}
>;

type GetPersistedDataFrameRowsByIdResultNode =
  | GetPersistedDataFrameRowsByIdDataFrameNode
  | GetPersistedDataFrameRowsByIdInaccessibleInfoNode;

export interface UseSelectedRowsAcrossPagesOutput {
  /** `currentPageDataFrame`, with any selected rows missing from the current page merged in. */
  dataFrame: DataFrame;
  /** True while resolving the current page's missing selected rows; overlaps in-flight requests coalesce. */
  isLoadingSelectedRows: boolean;
  /**
   * True if the most recent fetch for missing selected rows failed, meaning `dataFrame` may be missing
   * some selected rows' data. Callers should disable actions that act on the full selection while true,
   * rather than silently acting on a partial selection.
   */
  hasErrorLoadingSelectedRows: boolean;
}

function reportSelectedRowsLoadError(): void {
  pushToast({
    message: "Couldn't load some selected rows. Some actions may act on an incomplete selection.",
    type: ToastType.DANGER,
  });
}

function getMissingRowIds(currentPageDataFrame: DataFrame, selectedRowIds: Set<string>): Array<string> {
  if (selectedRowIds.size === 0) {
    return [];
  }
  const currentPageRowIds = new Set(currentPageDataFrame.rows.map((row) => rowIdAccessor(row)));
  return [...selectedRowIds].filter((rowId) => !currentPageRowIds.has(rowId));
}

// Namespaced by `dataFrameId` so a cached row can't leak into another data frame's merged results.
function getCacheKey(dataFrameId: string | undefined, rowId: string): string {
  return `${dataFrameId}:${rowId}`;
}

/**
 * Resolve every selected row's data, not just the rows on the currently-loaded page.
 *
 * In server-paginated mode, `currentPageDataFrame.rows` only contains the current page's rows, but
 * `selectedRowIds` accumulates across every page the user has visited. Bulk actions (copy/download/add to
 * worklist) filter `dataFrame.rows` by `selectedRowIds`, so a selected row whose data was never merged in
 * here would be silently dropped from those actions if it isn't on the current page.
 *
 * When `enabled` is false (client-side mode, where `currentPageDataFrame` already contains every row),
 * this is a no-op: no fetch, `dataFrame` is returned unchanged.
 */
export default function useSelectedRowsAcrossPages(
  dataFrameId: string | undefined,
  currentPageDataFrame: DataFrame,
  selectedRowIds: Set<string>,
  sortInput: DataFrameSortInputVariable | null,
  enabled: boolean
): UseSelectedRowsAcrossPagesOutput {
  const [fetchedRowsById, setFetchedRowsById] = useState<Map<string, DataFrame['rows'][number]>>(new Map());

  const missingRowIds = enabled ? getMissingRowIds(currentPageDataFrame, selectedRowIds) : [];
  // Sorted so the variables Apollo deep-compares across renders are stable even if `selectedRowIds`'s
  // insertion order shifts without its contents changing (which would otherwise look like new variables
  // and trigger a redundant refetch).
  const missingRowIdsToFetch = sortedBy(
    missingRowIds.filter((rowId) => !fetchedRowsById.has(getCacheKey(dataFrameId, rowId))),
    [(rowId) => rowId]
  );

  const result = useSkippableBenchlingQuery<
    GetPersistedDataFrameRowsById,
    GetPersistedDataFrameRowsByIdVariables
  >(GET_PERSISTED_DATA_FRAME_ROWS_BY_ID_QUERY, {
    variables: {dataFrameId: dataFrameId!, rowIds: missingRowIdsToFetch, sortInput},
    skip: dataFrameId == null || missingRowIdsToFetch.length === 0,
    fetchPolicy: 'network-only',
  });

  const fetchedDataFrameNode =
    result.type === QueryResultType.SUCCESS
      ? (result.data.dataFrame as GetPersistedDataFrameRowsByIdResultNode)
      : undefined;
  const hasErrorLoadingSelectedRows =
    result.type === QueryResultType.ERROR || isInaccessible(fetchedDataFrameNode);

  useEffect(() => {
    if (hasErrorLoadingSelectedRows) {
      reportSelectedRowsLoadError();
      return;
    }
    if (fetchedDataFrameNode == null) {
      return;
    }
    setFetchedRowsById((previousFetchedRowsById) => {
      const newFetchedRowsById = new Map(previousFetchedRowsById);
      for (const row of fetchedDataFrameNode.dataFrameJson.rows) {
        newFetchedRowsById.set(getCacheKey(dataFrameId, rowIdAccessor(row)), row);
      }
      return newFetchedRowsById;
    });
  }, [dataFrameId, fetchedDataFrameNode, hasErrorLoadingSelectedRows]);

  if (missingRowIds.length === 0) {
    return {
      dataFrame: currentPageDataFrame,
      isLoadingSelectedRows: false,
      hasErrorLoadingSelectedRows: false,
    };
  }

  const additionalRows = missingRowIds
    .map((rowId) => fetchedRowsById.get(getCacheKey(dataFrameId, rowId)))
    .filter((row): row is DataFrame['rows'][number] => row != null);

  return {
    dataFrame: {...currentPageDataFrame, rows: [...currentPageDataFrame.rows, ...additionalRows]},
    isLoadingSelectedRows: result.type === QueryResultType.PENDING,
    hasErrorLoadingSelectedRows,
  };
}
